import {
  Account,
  ActivityFilterModel,
  ActivityOrderBy,
  FeatureName,
  Issue,
  IssueMessageType,
  Media,
  replaceMentionPlaceholderWithNames,
  Severity,
  useIsFeatureEnabled,
  useTranslatedText,
} from '@gripp/shared-logic';
import { Pill, QrIcon } from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import { StatusTag } from '../crop-circle/Components/Tag';
import { TypeIcon, type TypeVariant } from '../crop-circle/Components/Type';
import { Icon } from '../crop-circle/icons';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { useTypedMediaItem } from '../media/useTypedMediaItem';
import { IssueCardOld } from './issueCard-old';

export interface IssueCardData {
  type: string;
  asset: {
    name: string;
    coverImage?: {
      id?: string;
      url?: string;
      extension?: string;
      status?: string;
      type?: string;
    };
  };
  workspace: {
    name: string;
  };
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  createdAt: string;
  effectiveCreationDate?: string;
  isPinned?: boolean;
  updatedAt: string;
  labels?: {
    id: string;
    name: string;
  }[];
  nextReminderDate?: string;
  userMentions?: Account[];
  latestMessage: {
    text: string;
    createdBy: Account;
    userMentions: Account[];
    type: IssueMessageType;
  };
}

export type IssueCardProps = {
  account: Account | null;
  activity: IssueCardData;
  showAssetName?: boolean;
  isUnread?: boolean;
  isMentionedInInbox?: boolean;
  filter: ActivityFilterModel;
};

const useIssueCardViewModel = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  const coverImageCandidate = activity.asset?.coverImage as unknown;
  const coverImageForTypedMedia =
    isMediaLike(coverImageCandidate) ? (coverImageCandidate as Media) : undefined;

  const { typedMediaItem: typedCoverImage } =
    useTypedMediaItem(coverImageForTypedMedia);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;

    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (props.isUnread && props.isMentionedInInbox) {
      return (
        <Text style={{ color: Colors.primary, fontSize: 14 }}>
          {t('activity.list.youAreMentioned')}
        </Text>
      );
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const latestMessageContent = getLatestMessageText();

  const renderLatestMessage = (textStyle: any, numberOfLines = 1) => {
    if (typeof latestMessageContent === 'string') {
      return (
        <Text variant="bodyMedium" style={textStyle} numberOfLines={numberOfLines}>
          {latestMessageContent}
        </Text>
      );
    }

    return latestMessageContent;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  };
};

function isMediaLike(value: unknown): value is Media {
  const v = value as any;
  return (
    !!v &&
    typeof v === 'object' &&
    typeof v.id === 'string' &&
    typeof v.extension === 'string' &&
    typeof v.type === 'string' &&
    typeof v.status === 'string'
  );
}

export const IssueCardBase = (props: IssueCardProps) => {
  const {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  } = useIssueCardViewModel(props);

  const severityToVariant: Record<Severity, TypeVariant> = {
    [Severity.Red]: 'attention',
    [Severity.Yellow]: 'blocker',
    [Severity.Green]: 'done',
    [Severity.Purple]: 'note',
    [Severity.Blue]: 'routines',
  };
  const severityVariant = severityToVariant[activity.severity] || 'attention';

  const coverImage = typedCoverImage || activity.asset?.coverImage;
  const coverUri =
    coverImage?.url && `${coverImage.url}`.trim().length > 0 ? coverImage.url : undefined;

  return (
    <View
      style={[styles.card, styles.cropCard, isHovered && styles.cropCardHovered]}
      {...hoverProps}
    >
      <View style={styles.cropBodyRow}>
        <View style={styles.cropImageWrapper}>
          {coverUri ? (
            <Image source={{ uri: coverUri }} style={styles.cropImage} />
          ) : (
            <View style={styles.cropImagePlaceholder}>
              <Icon name="QR" size={20} color={Colors.grayText} />
            </View>
          )}
        </View>

        <View style={styles.cropContent}>
          <View style={styles.cropWorkspaceRow}>
            <Text style={styles.cropWorkspace}>{activity.workspace.name}</Text>
          </View>

          <View style={styles.cropHeader}>
            <View style={styles.assetNamePillWrapper}>
              <Pill
                label={activity.asset?.name || activity.workspace.name}
                icon={QrIcon}
                testID="issue-asset-name-label"
                disabled={true}
              />
            </View>

            <View style={styles.cropMetaRight}>
              <Text variant="bodyMedium" style={styles.cropWhenText}>
                {auditData.when}
              </Text>
              {props.isUnread && <Icon name="Dot" size={12} color={Colors.primary} />}
            </View>
          </View>

          <View style={styles.cropTitleRow}>
            <View style={styles.cropLeadingIcons}>
              <TypeIcon variant={severityVariant} size={16} />
              {activity.nextReminderDate && (
                <View style={styles.cropReminderBadge}>
                  <Icon name="Clock B" size={12} color={Colors.grayText} />
                </View>
              )}
            </View>

            <Text style={styles.cropTitle} numberOfLines={1}>
              {getProcessedTitle()}
            </Text>

            {activity.isPinned && <Icon name="Pin" size={16} color={Colors.grayText} />}
          </View>

          <View style={styles.cropMessageRow}>
            {renderLatestMessage(styles.cropLastMessage, 2)}
          </View>

          {activity.labels && activity.labels.length > 0 && (
            <View style={styles.cropLabelsContainer}>
              {activity.labels.map((label) => (
                <StatusTag key={label.id} variant="neutral" text={label.name} />
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export const IssueCard = (props: IssueCardProps) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueCardOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  return <IssueCardBase {...props} />;
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    alignSelf: 'stretch',
    height: 'auto',
    padding: 16,
    ...Platform.select({
      web: { gap: 12 },
      default: { gap: 10 },
    }),
  },
  cropCard: {
    borderRadius: 12,
    backgroundColor: Colors.white,
    paddingVertical: 18,
    paddingHorizontal: 16,
    gap: 12,
    shadowColor: Colors.black,
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    ...Platform.select({
      android: {
        elevation: 2,
      },
    }),
  },
  cropCardHovered: {
    shadowOpacity: 0.08,
    ...Platform.select({
      android: {
        elevation: 4,
      },
    }),
  },
  cropBodyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  cropImageWrapper: {
    width: 72,
    height: 72,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: Colors.primaryGrayBackground,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cropImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  cropImagePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cropContent: {
    flex: 1,
    gap: 8,
  },
  cropWorkspaceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cropWorkspace: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  assetNamePillWrapper: {
    marginHorizontal: -8,
  },
  cropMetaRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 12,
  },
  cropWhenText: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cropLeadingIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  cropReminderBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cropTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.black,
  },
  cropMessageRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cropLastMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
    lineHeight: 20,
    flex: 1,
  },
  cropLabelsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
});
